import { filterVisibleListings } from '../../domain/listingVisibility';
import { addMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import { fetchCurrentUser } from '../../ducks/user.duck';
import { denormalisedResponseEntities } from '../../util/data';
import { storableError } from '../../util/errors';
import { createImageVariantConfig, types as sdkTypes } from '../../util/sdkLoader';
import { DIGITAL_PRODUCT_LISTING_TYPES } from '../../util/soulwise-marketplace/listingType';
import { datesSearchParams, fetchTimeslots } from '../SearchPage/SearchPage.duck';
import { fetchHandleByUserIdApi, fetchUserIdByHandleApi } from '../../util/api';
import { isUuid } from '../../util/uuid';

const { UUID } = sdkTypes;

const mergeListingQueryResponses = responses => {
  const acc = {
    data: {
      data: [],
      included: [],
      meta: {
        page: 0,
        paginationLimit: 0,
        perPage: 0,
        totalItems: 0,
        totalPages: 0,
      },
    },
  };
  const seenIds = new Set();
  responses.forEach(response => {
    const uniqueListings =
      response?.data?.data?.filter(item => {
        if (seenIds.has(item.id.uuid)) {
          return false;
        }
        seenIds.add(item.id.uuid);
        return true;
      }) || [];

    acc.data.data.push(...uniqueListings);
    acc.data.included.push(...(response?.data?.included || []));
    acc.data.meta.totalItems += response?.data?.meta?.totalItems || 0;
    acc.data.meta.totalPages = Math.max(
      acc.data.meta.totalPages,
      response?.data?.meta?.totalPages || 0
    );
  });
  return acc;
};

// ================ Action types ================ //

export const SET_INITIAL_STATE = 'app/ProfilePage/SET_INITIAL_STATE';

export const SHOW_USER_REQUEST = 'app/ProfilePage/SHOW_USER_REQUEST';
export const SHOW_USER_SUCCESS = 'app/ProfilePage/SHOW_USER_SUCCESS';
export const SHOW_USER_ERROR = 'app/ProfilePage/SHOW_USER_ERROR';

export const QUERY_LISTINGS_REQUEST = 'app/ProfilePage/QUERY_LISTINGS_REQUEST';
export const QUERY_LISTINGS_SUCCESS = 'app/ProfilePage/QUERY_LISTINGS_SUCCESS';
export const QUERY_LISTINGS_ERROR = 'app/ProfilePage/QUERY_LISTINGS_ERROR';

export const QUERY_REVIEWS_REQUEST = 'app/ProfilePage/QUERY_REVIEWS_REQUEST';
export const QUERY_REVIEWS_SUCCESS = 'app/ProfilePage/QUERY_REVIEWS_SUCCESS';
export const QUERY_REVIEWS_ERROR = 'app/ProfilePage/QUERY_REVIEWS_ERROR';

export const SET_RESOLVED_HANDLE = 'app/ProfilePage/SET_RESOLVED_HANDLE';

// ================ Reducer ================ //

const initialState = {
  userId: null,
  userListingRefs: [],
  userShowError: null,
  queryListingsError: null,
  reviews: [],
  queryReviewsError: null,
  resolvedHandle: null,
};

export default function profilePageReducer(state = initialState, action = {}) {
  const { type, payload } = action;
  switch (type) {
    case SET_INITIAL_STATE:
      return { ...initialState };
    case SHOW_USER_REQUEST:
      return { ...state, userShowError: null, userId: payload.userId };
    case SHOW_USER_SUCCESS:
      return state;
    case SHOW_USER_ERROR:
      return { ...state, userShowError: payload };

    case QUERY_LISTINGS_REQUEST:
      return {
        ...state,

        // Empty listings only when user id changes
        userListingRefs: payload.userId === state.userId ? state.userListingRefs : [],

        queryListingsError: null,
      };
    case QUERY_LISTINGS_SUCCESS:
      return { ...state, userListingRefs: payload.listingRefs };
    case QUERY_LISTINGS_ERROR:
      return { ...state, userListingRefs: [], queryListingsError: payload };
    case QUERY_REVIEWS_REQUEST:
      return { ...state, queryReviewsError: null };
    case QUERY_REVIEWS_SUCCESS:
      return { ...state, reviews: payload };
    case QUERY_REVIEWS_ERROR:
      return { ...state, reviews: [], queryReviewsError: payload };

    case SET_RESOLVED_HANDLE:
      return { ...state, resolvedHandle: payload };

    default:
      return state;
  }
}

// ================ Action creators ================ //

export const setInitialState = () => ({
  type: SET_INITIAL_STATE,
});

export const showUserRequest = userId => ({
  type: SHOW_USER_REQUEST,
  payload: { userId },
});

export const showUserSuccess = () => ({
  type: SHOW_USER_SUCCESS,
});

export const showUserError = e => ({
  type: SHOW_USER_ERROR,
  error: true,
  payload: e,
});

export const queryListingsRequest = userId => ({
  type: QUERY_LISTINGS_REQUEST,
  payload: { userId },
});

export const queryListingsSuccess = listingRefs => ({
  type: QUERY_LISTINGS_SUCCESS,
  payload: { listingRefs },
});

export const queryListingsError = e => ({
  type: QUERY_LISTINGS_ERROR,
  error: true,
  payload: e,
});

export const queryReviewsRequest = () => ({
  type: QUERY_REVIEWS_REQUEST,
});

export const queryReviewsSuccess = reviews => ({
  type: QUERY_REVIEWS_SUCCESS,
  payload: reviews,
});

export const queryReviewsError = e => ({
  type: QUERY_REVIEWS_ERROR,
  error: true,
  payload: e,
});

export const setResolvedHandle = handle => ({
  type: SET_RESOLVED_HANDLE,
  payload: handle || null,
});

// ================ Thunks ================ //

export const queryUserListings = (userId, config) => (dispatch, getState, sdk) => {
  dispatch(queryListingsRequest(userId));

  const {
    aspectWidth = 1,
    aspectHeight = 1,
    variantPrefix = 'listing-card',
  } = config.layout.listingImage;
  const aspectRatio = aspectHeight / aspectWidth;
  const { immediateRangeDateParams, futureRangeDateParams } = datesSearchParams('', '');
  const dateParams = {
    start: immediateRangeDateParams.start,
    end: futureRangeDateParams.end,
    availability: immediateRangeDateParams.availability,
    minDuration: immediateRangeDateParams.minDuration,
  };

  const baseQuery = {
    author_id: userId,
    include: ['author', 'images'],
    'fields.image': [`variants.${variantPrefix}`, `variants.${variantPrefix}-2x`],
    ...createImageVariantConfig(`${variantPrefix}`, 400, aspectRatio),
    ...createImageVariantConfig(`${variantPrefix}-2x`, 800, aspectRatio),
  };

  const digitalListingTypesParam = DIGITAL_PRODUCT_LISTING_TYPES.join(',');

  return Promise.all([
    sdk.listings.query({
      ...baseQuery,
      ...dateParams,
    }),
    sdk.listings.query({
      ...baseQuery,
      pub_listingType: digitalListingTypesParam,
    }),
  ])
    .then(([bookableResponse, digitalResponse]) => {
      const mergedResponse = mergeListingQueryResponses([bookableResponse, digitalResponse]);

      const listings = mergedResponse.data.data;
      const listingRefs = filterVisibleListings(listings).map(({ id, type }) => ({ id, type }));
      const listingIds = listingRefs?.map(ref => ref.id);

      dispatch(addMarketplaceEntities(bookableResponse));
      dispatch(addMarketplaceEntities(digitalResponse));
      dispatch(queryListingsSuccess(listingRefs));
      dispatch(
        fetchTimeslots(listingIds, {
          start: immediateRangeDateParams.start,
          end: futureRangeDateParams.end,
        })
      );
      return mergedResponse;
    })
    .catch(e => dispatch(queryListingsError(storableError(e))));
};

export const queryUserReviews = userId => (dispatch, getState, sdk) => {
  sdk.reviews
    .query({
      subject_id: userId,
      state: 'public',
      include: ['author', 'author.profileImage'],
      'fields.image': ['variants.square-small', 'variants.square-small2x'],
    })
    .then(response => {
      const reviews = denormalisedResponseEntities(response);
      dispatch(queryReviewsSuccess(reviews));
    })
    .catch(e => dispatch(queryReviewsError(e)));
};

export const showUser = (userId, config) => (dispatch, getState, sdk) => {
  dispatch(showUserRequest(userId));
  return sdk.users
    .show({
      id: userId,
      include: ['profileImage'],
      'fields.image': ['variants.square-small', 'variants.square-small2x'],
    })
    .then(response => {
      const userFields = config?.user?.userFields;
      const sanitizeConfig = { userFields };
      dispatch(addMarketplaceEntities(response, sanitizeConfig));
      dispatch(showUserSuccess());
      return response;
    })
    .catch(e => dispatch(showUserError(storableError(e))));
};

export const loadData = (params, search, config) => (dispatch, getState, sdk) => {
  // Clear state so that previously loaded data is not visible
  // in case this page load fails.
  dispatch(setInitialState());

  const resolveUserId = () => {
    if (isUuid(params.id)) {
      return Promise.resolve(params.id);
    }
    return fetchUserIdByHandleApi(params.id).then(result => result?.data?.userId);
  };

  return resolveUserId()
    .then(resolvedId => {
      if (!resolvedId) {
        const err = new Error('User not found');
        dispatch(showUserError(storableError(err)));
        return null;
      }
      const userId = new UUID(resolvedId);
      return Promise.all([
        dispatch(fetchCurrentUser()),
        dispatch(showUser(userId, config)),
        dispatch(queryUserListings(userId, config)),
        dispatch(queryUserReviews(userId)),
        fetchHandleByUserIdApi(resolvedId)
          .then(result => {
            if (result?.data?.handle) {
              dispatch(setResolvedHandle(result.data.handle));
            }
            return result;
          })
          .catch(() => null),
      ]);
    })
    .catch(e => {
      dispatch(showUserError(storableError(e)));
      return null;
    });
};
